/**
 * Extract/OnlyOffice-fileType aus Name, NC-Pfad, URL oder MIME.
 * Keine Vite-/PB-Imports — Unit-Tests laufen per node --experimental-strip-types.
 */

export function fileExtensionFromLeaf(name: string): string {
  const m = String(name || '')
    .trim()
    .toLowerCase()
    .match(/\.([a-z0-9]+)$/);
  return m ? m[1] : '';
}

/** Letztes Pfadsegment ohne Query/Fragment — NC-Pfad und Share-URL. */
export function portalDocumentBasename(pathOrUrl: string): string {
  const p = String(pathOrUrl || '').trim().split('?')[0]?.split('#')[0] ?? '';
  const parts = p.replace(/\\/g, '/').split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1]! : '';
}

const MIME_TO_EXTRACT_FILETYPE: Record<string, string> = {
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
  'application/msword': 'doc',
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.oasis.opendocument.text': 'odt',
  'application/vnd.oasis.opendocument.spreadsheet': 'ods',
  'application/rtf': 'rtf',
  'text/rtf': 'rtf',
  'text/plain': 'txt',
  'text/html': 'html',
  'text/csv': 'csv',
  'text/markdown': 'md',
};

/** MIME → Extract/OnlyOffice-fileType (ohne Endung in display_name). */
export function fileTypeFromMime(mime?: string | null): string {
  const raw = String(mime || '')
    .trim()
    .toLowerCase()
    .split(';')[0]
    ?.trim();
  if (!raw) return '';
  if (MIME_TO_EXTRACT_FILETYPE[raw]) return MIME_TO_EXTRACT_FILETYPE[raw];
  if (raw.includes('wordprocessingml') || raw.includes('officedocument.word')) return 'docx';
  if (raw === 'application/msword') return 'doc';
  if (raw.includes('pdf')) return 'pdf';
  if (raw === 'image/jpeg' || raw === 'image/jpg') return 'jpg';
  if (raw === 'image/png') return 'png';
  if (raw.includes('spreadsheetml') || raw.includes('excel')) return 'xlsx';
  if (raw.includes('opendocument.text')) return 'odt';
  if (raw.startsWith('text/html')) return 'html';
  if (raw.startsWith('text/plain')) return 'txt';
  if (raw.startsWith('text/csv')) return 'csv';
  return '';
}

const PORTAL_IMAGE_EXTENSIONS = new Set(['jpg', 'jpeg', 'png']);
const PORTAL_ID_IMAGE_FILENAME_RE =
  /(?:^|[\s._-])(?:ausweis|personalausweis|reisepass|passport|identitaetskarte|identitätskarte|id[\s._-]?card|fuehrerschein|führerschein)(?:[\s._-]|$)/i;

export const PORTAL_IMAGE_OCR_NOTICE =
  'Fotos (JPG/PNG) werden lokal nur auf lesbare Schrift geprüft. Keine Ausweis-, Pass- oder Führerscheinfotos hochladen; ARKO deutet keine Szene.';
export const PORTAL_LOCAL_OCR_MARKER = 'portal-ocr:local-pdf-image-v1';

export function isPortalImageFile(args: {
  name?: string | null;
  mimeType?: string | null;
}): boolean {
  const ext = fileExtensionFromLeaf(args.name || '');
  const mime = String(args.mimeType || '').trim().toLowerCase().split(';')[0];
  return PORTAL_IMAGE_EXTENSIONS.has(ext) || mime === 'image/jpeg' || mime === 'image/png';
}

/**
 * Filename-Heuristik als DSGVO-Gate. Kein Bildinhalt wird fuer die Entscheidung
 * an ein Modell geschickt; der Server wiederholt denselben Guard.
 */
export function portalImageUploadPolicyError(args: {
  name?: string | null;
  mimeType?: string | null;
}): string | null {
  if (!isPortalImageFile(args)) return null;
  const leaf = portalDocumentBasename(args.name || '');
  if (!PORTAL_ID_IMAGE_FILENAME_RE.test(leaf)) return null;
  return 'Ausweis-, Pass- und Führerscheinfotos sind in der Akte nicht zugelassen. Bitte keine Identitätsdokumente hochladen.';
}

/**
 * Extract/OnlyOffice-fileType: NC-Pfad und Originalname vor display_name/URL
 * (Share-Download und Titel ohne Endung, Datei am Pfad z. B. …/foo.docx).
 */
export function resolvePortalDocumentFileType(args: {
  displayName?: string | null;
  nextcloudPath?: string | null;
  documentUrl?: string | null;
  mimeType?: string | null;
  originalName?: string | null;
}): string {
  const candidates = [
    args.nextcloudPath,
    args.originalName,
    args.displayName,
    args.documentUrl,
  ];
  for (const c of candidates) {
    const leaf = portalDocumentBasename(c || '');
    const ext = fileExtensionFromLeaf(leaf || String(c || ''));
    if (ext) return ext;
  }
  return fileTypeFromMime(args.mimeType);
}

/** UI-/NC-Name eines frisch angelegten leeren Word (Extract erst nach echtem Inhalt). */
export function isLikelyBlankPortalDocumentName(name?: string | null): boolean {
  return /^(?:Neues Dokument|Leeres Dokument)\b/i.test(String(name || '').trim());
}

/**
 * Extract-fileType: aufgelöst aus Pfad/MIME, sonst `.docx` für leeres/neues Word.
 * Leer = Extract nicht senden (sonst 415/422).
 */
export function resolvePortalExtractFileType(args: {
  displayName?: string | null;
  nextcloudPath?: string | null;
  documentUrl?: string | null;
  mimeType?: string | null;
  originalName?: string | null;
}): string {
  const resolved = resolvePortalDocumentFileType(args);
  if (resolved) return resolved;
  const name = args.displayName || args.originalName || '';
  if (isLikelyBlankPortalDocumentName(name)) return 'docx';
  return '';
}

/** Schreiben-Upload: Dateiname immer mit .docx (Share-URL bleibt ohne Endung). */
export function ensurePortalDocxFileName(name: string): string {
  const trimmed = String(name || '').trim() || 'Schreiben.docx';
  if (/\.docx$/i.test(trimmed)) return trimmed;
  const withoutExt = trimmed.replace(/\.(pdf|doc)$/i, '');
  return `${withoutExt || 'Schreiben'}.docx`;
}
